import React from 'react';
import { Button, ButtonAppearance, Heading, Text } from '@atlassian/learning-components';
import { useNavigate } from 'react-router-dom';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';
import { css } from '@compiled/react';

import bookIcon from 'src/assets/images/learningEmptyStateIcons/book.svg?url';
import searchIcon from 'src/assets/images/learningEmptyStateIcons/search.svg?url';
import ribbonIcon from 'src/assets/images/learningEmptyStateIcons/ribbon.svg?url';

export type EmptyStateType = (typeof EMPTY_STATE_TYPES)[keyof typeof EMPTY_STATE_TYPES];

interface LearningEmptyStateProps {
  emptyStateType: EmptyStateType;
}

interface EmptyStateConfig {
  [key: string]: {
    imageUrl: string;
    header: string;
    description: string;
    buttonLabel: string;
  };
}

export const EMPTY_STATE_TYPES = {
  ON_DEMAND_EMPTY_FILTER: 'on-demand-empty-filter',
  ON_DEMAND_EMPTY_RESULTS: 'on-demand-empty-results',
  LIVE_TRAINING_EMPTY_FILTER: 'live-training-empty-filter',
  LIVE_TRAINING_EMPTY_RESULTS: 'live-training-empty-results',
  CERTIFICATIONS_EMPTY: 'certifications-empty',
} as const;

const EMPTY_STATE_MAP: EmptyStateConfig = {
  [EMPTY_STATE_TYPES.ON_DEMAND_EMPTY_FILTER]: {
    imageUrl: searchIcon,
    header: 'Let’s try again',
    description: 'We couldn’t find any matches. Try adjusting your filters or search our catalog.',
    buttonLabel: 'Browse catalog',
  },
  [EMPTY_STATE_TYPES.ON_DEMAND_EMPTY_RESULTS]: {
    imageUrl: bookIcon,
    header: 'Get started',
    description:
      'Once you start, save, or complete content, it’ll show up here so you can easily pick up where you left off.',
    buttonLabel: 'Browse catalog',
  },
  [EMPTY_STATE_TYPES.LIVE_TRAINING_EMPTY_FILTER]: {
    imageUrl: searchIcon,
    header: 'Let’s try again',
    description: 'We couldn’t find any matches. Try adjusting your filters or search our calendar.',
    buttonLabel: 'Browse calendar',
  },
  [EMPTY_STATE_TYPES.LIVE_TRAINING_EMPTY_RESULTS]: {
    imageUrl: bookIcon,
    header: 'Sign up for a class',
    description: 'You don’t have any upcoming, saved or completed classes yet. Find a class to get started.',
    buttonLabel: 'Browse calendar',
  },
  [EMPTY_STATE_TYPES.CERTIFICATIONS_EMPTY]: {
    imageUrl: ribbonIcon,
    header: 'Manage your credentials',
    description: 'Schedule an exam, upload documents, and manage your personal info through the credential portal.',
    buttonLabel: 'Go to portal',
  },
};

export const LearningEmptyState = ({ emptyStateType }: LearningEmptyStateProps) => {
  const navigate = useNavigate();

  const handleCtaClick = () => {
    switch (emptyStateType) {
      case EMPTY_STATE_TYPES.LIVE_TRAINING_EMPTY_FILTER:
      case EMPTY_STATE_TYPES.LIVE_TRAINING_EMPTY_RESULTS:
        navigate('/calendar');
        break;
      case EMPTY_STATE_TYPES.ON_DEMAND_EMPTY_FILTER:
      case EMPTY_STATE_TYPES.ON_DEMAND_EMPTY_RESULTS:
        navigate('/catalog');
        break;
      case EMPTY_STATE_TYPES.CERTIFICATIONS_EMPTY:
        // eslint-disable-next-line no-console
        console.log('TODO: navigate to portal');
        break;
      default:
        break;
    }
  };

  return (
    <div css={containerStyles}>
      <img src={EMPTY_STATE_MAP[emptyStateType].imageUrl} alt="Empty state illustration" css={imageStyles} />
      <Heading level={3}>{EMPTY_STATE_MAP[emptyStateType].header}</Heading>
      <Text>{EMPTY_STATE_MAP[emptyStateType].description}</Text>
      <Button
        label={EMPTY_STATE_MAP[emptyStateType].buttonLabel}
        appearance={ButtonAppearance.SECONDARY}
        onClick={handleCtaClick}
      />
    </div>
  );
};

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: token('space.300'),
  maxWidth: '438px',
  marginTop: `calc(${token('space.600')} + ${token('space.500')})`,
  marginLeft: 'auto',
  marginRight: 'auto',

  [media.below.md]: {
    marginTop: token('space.400'),
  },

  '& > *': {
    textAlign: 'center',
  },
});

const imageStyles = css({
  width: '200px',
  height: '200px',
  marginBottom: token('space.200'),
});
